import { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { db } from "../firebase/config";
import {
  collection,
  doc,
  updateDoc,
  onSnapshot,
  deleteDoc,
  addDoc,
} from "firebase/firestore";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Tooltip,
  LinearProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Typography,
  Container,
  Box,
  InputAdornment,
} from "@mui/material";
import {
  Edit,
  Save,
  Cancel,
  Delete,
  Add,
  CloudUpload,
  Visibility,
  AttachFile,
} from "@mui/icons-material";
import { toast } from "react-toastify";

export default function KpiDetail() {
  const { perusahaan, tahun } = useParams();
  const location = useLocation();
  const role = location.state?.role || "user";

  const [data, setData] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(true);
  const [newKpi, setNewKpi] = useState({
    indikator: "",
    keterangan: "",
    satuan: "",
    bobot: "",
    target: "",
  });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [kpiToDelete, setKpiToDelete] = useState(null);
  const [uploadingEvidence, setUploadingEvidence] = useState(false);

  // Fetch KPI data
  useEffect(() => {
    if (!perusahaan || !tahun) return;

    const unsub = onSnapshot(
      collection(db, "kpi_collections", perusahaan, tahun),
      (snapshot) => {
        const kpiData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setData(kpiData);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching KPI data:", error);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [perusahaan, tahun]);

  // Handle edit
  const handleEdit = (item) => {
    setEditingId(item.id);
    setEditData({
      indikator: item.indikator || "",
      keterangan: item.keterangan || "",
      satuan: item.satuan || "",
      bobot: item.bobot || "",
      target: item.target || "",
      target_sd_sekarang: item.target_sd_sekarang || "",
      realisasi: item.realisasi || "",
      pencapaian: item.pencapaian || "",
      nilai: item.nilai || "",
      evidence: item.evidence || "",
    });
  };

  // Auto calculate pencapaian and nilai
  useEffect(() => {
    if (
      editingId &&
      editData.realisasi &&
      editData.target_sd_sekarang &&
      editData.bobot
    ) {
      const realisasi = parseFloat(editData.realisasi) || 0;
      const targetNow = parseFloat(editData.target_sd_sekarang) || 0;
      const bobot = parseFloat(editData.bobot) || 0;

      const pencapaian = targetNow > 0 ? (realisasi / targetNow) * 100 : 0;
      const nilai = pencapaian * (bobot / 100);

      setEditData((prev) => ({
        ...prev,
        pencapaian: pencapaian.toFixed(2),
        nilai: nilai.toFixed(2),
      }));
    }
  }, [
    editData.realisasi,
    editData.target_sd_sekarang,
    editData.bobot,
    editingId,
  ]);

  // Handle save
  const handleSave = async (id) => {
    try {
      await updateDoc(
        doc(db, "kpi_collections", perusahaan, tahun, id),
        editData
      );
      setEditingId(null);
      toast.success("KPI berhasil diperbarui");
    } catch (error) {
      console.error("Update Error:", error);
      toast.error("Gagal memperbarui KPI");
    }
  };

  // Handle add new KPI
  const handleAddKpi = async () => {
    try {
      await addDoc(collection(db, "kpi_collections", perusahaan, tahun), {
        ...newKpi,
        target_sd_sekarang: "",
        realisasi: "",
        pencapaian: "",
        nilai: "",
        evidence: "",
      });
      setNewKpi({
        indikator: "",
        keterangan: "",
        satuan: "",
        bobot: "",
        target: "",
      });
      toast.success("KPI baru berhasil ditambahkan");
    } catch (err) {
      console.error("Gagal menambahkan KPI", err);
      toast.error("Gagal menambahkan KPI");
    }
  };

  // Handle delete confirmation
  const handleDeleteClick = (id) => {
    setKpiToDelete(id);
    setDeleteConfirmOpen(true);
  };

  // Handle confirmed delete
  const handleConfirmDelete = async () => {
    try {
      await deleteDoc(
        doc(db, "kpi_collections", perusahaan, tahun, kpiToDelete)
      );
      toast.success("KPI berhasil dihapus");
      setDeleteConfirmOpen(false);
    } catch (error) {
      console.error("Error deleting KPI:", error);
      toast.error("Gagal menghapus KPI");
    }
  };

  // Handle evidence upload
  const handleUploadEvidence = (e, kpiId) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingEvidence(true);

    // TODO: Implement actual file upload logic here
    // This is just a mock implementation
    setTimeout(() => {
      setUploadingEvidence(false);
      toast.success("Evidence berhasil diupload");
      // Update the document with file URL
      setEditData((prev) => ({
        ...prev,
        evidence: "https://example.com/uploaded-file.pdf",
      }));
    }, 2000);
  };

  // Format values based on unit
  const formatValue = (value, satuan) => {
    if (!value) return "-";

    const satuanLower = satuan?.toLowerCase() || "";

    if (satuanLower.includes("persen")) {
      return `${value}%`;
    }

    if (satuanLower.includes("rupiah")) {
      return `Rp ${parseFloat(value).toLocaleString("id-ID")}`;
    }

    return value;
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 2 }}>
        <LinearProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <Typography
        variant="h5"
        component="h1"
        gutterBottom
        sx={{ mb: 2, fontSize: "1.2rem" }}
      >
        KPI {perusahaan} Tahun {tahun}
      </Typography>
      <TableContainer
        component={Paper}
        elevation={1}
        sx={{ maxHeight: "calc(100vh - 200px)" }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{ width: 40, fontSize: "0.75rem", fontWeight: "bold" }}
              >
                No
              </TableCell>
              <TableCell sx={{ fontSize: "0.75rem", fontWeight: "bold" }}>
                Indikator
              </TableCell>
              <TableCell sx={{ fontSize: "0.75rem", fontWeight: "bold" }}>
                Keterangan
              </TableCell>
              <TableCell
                sx={{ fontSize: "0.75rem", fontWeight: "bold", width: 80 }}
              >
                Satuan
              </TableCell>
              <TableCell
                sx={{ fontSize: "0.75rem", fontWeight: "bold", width: 80 }}
              >
                Bobot
              </TableCell>
              <TableCell
                sx={{ fontSize: "0.75rem", fontWeight: "bold", width: 100 }}
              >
                Target
              </TableCell>
              <TableCell
                sx={{ fontSize: "0.75rem", fontWeight: "bold", width: 120 }}
              >
                Target s.d Sekarang
              </TableCell>
              <TableCell
                sx={{ fontSize: "0.75rem", fontWeight: "bold", width: 100 }}
              >
                Realisasi
              </TableCell>
              <TableCell
                sx={{ fontSize: "0.75rem", fontWeight: "bold", width: 100 }}
              >
                Pencapaian
              </TableCell>
              <TableCell
                sx={{ fontSize: "0.75rem", fontWeight: "bold", width: 80 }}
              >
                Nilai
              </TableCell>
              <TableCell
                sx={{ fontSize: "0.75rem", fontWeight: "bold", width: 120 }}
              >
                Evidence
              </TableCell>
              {role === "admin" && (
                <TableCell
                  sx={{ fontSize: "0.75rem", fontWeight: "bold", width: 100 }}
                >
                  Aksi
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, idx) => (
              <TableRow key={row.id} hover>
                <TableCell sx={{ fontSize: "0.75rem" }}>{idx + 1}</TableCell>

                {/* Indikator */}
                <TableCell sx={{ fontSize: "0.75rem" }}>
                  {editingId === row.id ? (
                    <TextField
                      size="small"
                      fullWidth
                      value={editData.indikator}
                      onChange={(e) =>
                        setEditData({ ...editData, indikator: e.target.value })
                      }
                      sx={{ fontSize: "0.75rem" }}
                    />
                  ) : (
                    row.indikator
                  )}
                </TableCell>

                {/* Keterangan */}
                <TableCell sx={{ fontSize: "0.75rem" }}>
                  {editingId === row.id ? (
                    <TextField
                      size="small"
                      fullWidth
                      value={editData.keterangan}
                      onChange={(e) =>
                        setEditData({ ...editData, keterangan: e.target.value })
                      }
                      sx={{ fontSize: "0.75rem" }}
                    />
                  ) : (
                    row.keterangan
                  )}
                </TableCell>

                {/* Satuan */}
                <TableCell sx={{ fontSize: "0.75rem" }}>
                  {editingId === row.id ? (
                    <TextField
                      size="small"
                      value={editData.satuan}
                      onChange={(e) =>
                        setEditData({ ...editData, satuan: e.target.value })
                      }
                      sx={{ fontSize: "0.75rem" }}
                    />
                  ) : (
                    row.satuan
                  )}
                </TableCell>

                {/* Bobot */}
                <TableCell sx={{ fontSize: "0.75rem" }}>
                  {editingId === row.id ? (
                    <TextField
                      size="small"
                      type="number"
                      value={editData.bobot}
                      onChange={(e) =>
                        setEditData({ ...editData, bobot: e.target.value })
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">%</InputAdornment>
                        ),
                      }}
                      sx={{ fontSize: "0.75rem" }}
                    />
                  ) : (
                    `${row.bobot}%`
                  )}
                </TableCell>

                {/* Target */}
                <TableCell sx={{ fontSize: "0.75rem" }}>
                  {editingId === row.id ? (
                    <TextField
                      size="small"
                      value={editData.target}
                      onChange={(e) =>
                        setEditData({ ...editData, target: e.target.value })
                      }
                      sx={{ fontSize: "0.75rem" }}
                    />
                  ) : (
                    formatValue(row.target, row.satuan)
                  )}
                </TableCell>

                {/* Target s.d Sekarang */}
                <TableCell sx={{ fontSize: "0.75rem" }}>
                  {editingId === row.id ? (
                    <TextField
                      size="small"
                      value={editData.target_sd_sekarang}
                      onChange={(e) =>
                        setEditData({
                          ...editData,
                          target_sd_sekarang: e.target.value,
                        })
                      }
                      sx={{ fontSize: "0.75rem" }}
                    />
                  ) : (
                    formatValue(row.target_sd_sekarang, row.satuan)
                  )}
                </TableCell>

                {/* Realisasi */}
                <TableCell sx={{ fontSize: "0.75rem" }}>
                  {editingId === row.id ? (
                    <TextField
                      size="small"
                      value={editData.realisasi}
                      onChange={(e) =>
                        setEditData({ ...editData, realisasi: e.target.value })
                      }
                      sx={{ fontSize: "0.75rem" }}
                    />
                  ) : (
                    formatValue(row.realisasi, row.satuan)
                  )}
                </TableCell>

                {/* Pencapaian */}
                <TableCell sx={{ fontSize: "0.75rem" }}>
                  {editingId === row.id ? (
                    <TextField
                      size="small"
                      value={editData.pencapaian}
                      onChange={(e) =>
                        setEditData({ ...editData, pencapaian: e.target.value })
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">%</InputAdornment>
                        ),
                      }}
                      sx={{ fontSize: "0.75rem" }}
                    />
                  ) : (
                    `${row.pencapaian}%`
                  )}
                </TableCell>

                {/* Nilai */}
                <TableCell sx={{ fontSize: "0.75rem" }}>
                  {editingId === row.id ? (
                    <TextField
                      size="small"
                      value={editData.nilai}
                      onChange={(e) =>
                        setEditData({ ...editData, nilai: e.target.value })
                      }
                      sx={{ fontSize: "0.75rem" }}
                    />
                  ) : (
                    row.nilai
                  )}
                </TableCell>

                {/* Evidence */}
                <TableCell sx={{ fontSize: "0.75rem" }}>
                  {row.evidence ? (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Tooltip title="Lihat Evidence">
                        <IconButton
                          href={row.evidence}
                          target="_blank"
                          size="small"
                          sx={{ p: 0, mr: 1 }}
                        >
                          <Visibility fontSize="small" color="primary" />
                        </IconButton>
                      </Tooltip>
                      {editingId === row.id && (
                        <>
                          <input
                            accept=".pdf,.doc,.docx,.jpg,.png"
                            style={{ display: "none" }}
                            id={`evidence-upload-${row.id}`}
                            type="file"
                            onChange={(e) => handleUploadEvidence(e, row.id)}
                          />
                          <label htmlFor={`evidence-upload-${row.id}`}>
                            <Tooltip title="Upload Evidence Baru">
                              <IconButton
                                component="span"
                                size="small"
                                sx={{ p: 0 }}
                              >
                                <AttachFile fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </label>
                        </>
                      )}
                    </Box>
                  ) : editingId === row.id ? (
                    <>
                      <input
                        accept=".pdf,.doc,.docx,.jpg,.png"
                        style={{ display: "none" }}
                        id={`evidence-upload-${row.id}`}
                        type="file"
                        onChange={(e) => handleUploadEvidence(e, row.id)}
                      />
                      <label htmlFor={`evidence-upload-${row.id}`}>
                        <Button
                          variant="outlined"
                          size="small"
                          component="span"
                          startIcon={<CloudUpload />}
                          disabled={uploadingEvidence}
                          sx={{ fontSize: "0.75rem" }}
                        >
                          {uploadingEvidence ? "Uploading..." : "Upload"}
                        </Button>
                      </label>
                    </>
                  ) : (
                    "-"
                  )}
                </TableCell>

                {/* Action Buttons */}
                {role === "admin" && (
                  <TableCell sx={{ fontSize: "0.75rem" }}>
                    <Box sx={{ display: "flex" }}>
                      {editingId === row.id ? (
                        <>
                          <Tooltip title="Simpan">
                            <IconButton
                              onClick={() => handleSave(row.id)}
                              color="primary"
                              size="small"
                              sx={{ p: 0.5 }}
                            >
                              <Save fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Batal">
                            <IconButton
                              onClick={() => setEditingId(null)}
                              color="error"
                              size="small"
                              sx={{ p: 0.5, ml: 0.5 }}
                            >
                              <Cancel fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <>
                          <Tooltip title="Edit">
                            <IconButton
                              onClick={() => handleEdit(row)}
                              color="primary"
                              size="small"
                              sx={{ p: 0.5 }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Hapus">
                            <IconButton
                              onClick={() => handleDeleteClick(row.id)}
                              color="error"
                              size="small"
                              sx={{ p: 0.5, ml: 0.5 }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle sx={{ fontSize: "1rem" }}>
          Konfirmasi Hapus KPI
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.875rem" }}>
            Apakah Anda yakin ingin menghapus KPI ini? Tindakan ini tidak dapat
            dibatalkan.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            size="small"
            sx={{ fontSize: "0.75rem" }}
          >
            Batal
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            size="small"
            sx={{ fontSize: "0.75rem" }}
          >
            Hapus
          </Button>
        </DialogActions>
      </Dialog>
      {data.length > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mt: 2,
            mb: 4,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 2,
              background: "linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)",
              color: "white",
              borderRadius: "12px",
              minWidth: "200px",
              textAlign: "center",
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: "bold",
                fontSize: "0.9rem",
                opacity: 0.9,
                mb: 0.5,
              }}
            >
              TOTAL NILAI KPI
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontWeight: "bold",
                fontSize: "2.2rem",
                lineHeight: 1,
              }}
            >
              {data
                .reduce((sum, item) => sum + (parseFloat(item.nilai) || 0), 0)
                .toFixed(2)}
            </Typography>
            <Box
              sx={{
                mt: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.7rem",
                  opacity: 0.8,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {perusahaan} • {tahun}
              </Typography>
            </Box>
          </Paper>
        </Box>
      )}
    </Container>
  );
}
